import { themeAtom } from "../../../shared/atoms";
import { AdminSidebarDrawerNavProps } from "../../models/admin.models";
import { adminStyles } from "../../styles/admin.styles";
import { ADMIN_SIDEBAR_POPOVER_VIEWPORT_PADDING } from "../../utils/admin-sidebar.constants";
import AdminSidebarIcon from "./AdminSidebarIcon";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useLayoutEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const AdminSidebarDrawerNav = ({
  groupId,
  label,
  sections,
  isCollapsed,
  isActivePath,
  isDrawerOpen,
  isPopoverOpen,
  onToggleDrawer,
  onTogglePopover,
}: AdminSidebarDrawerNavProps) => {
  const theme = useAtomValue(themeAtom);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const activeStyle = isActivePath
    ? {
        background: theme.sideBarComponent.background.activeLink,
        color: theme.sideBarComponent.iconAndText.active,
      }
    : undefined;

  // Keep collapsed menus outside the scrolling rail and within the viewport.
  useLayoutEffect(() => {
    if (!isCollapsed || !isPopoverOpen) {
      return;
    }

    const positionPopover = () => {
      const toggle = toggleRef.current;
      const popover = popoverRef.current;
      const sidebar = toggle?.closest("aside");

      if (!toggle || !popover || !sidebar) {
        return;
      }

      const toggleBounds = toggle.getBoundingClientRect();
      const maxTop = window.innerHeight - popover.offsetHeight - ADMIN_SIDEBAR_POPOVER_VIEWPORT_PADDING;
      popover.style.top = `${Math.max(ADMIN_SIDEBAR_POPOVER_VIEWPORT_PADDING, Math.min(toggleBounds.top, maxTop))}px`;
      popover.style.left = `${sidebar.getBoundingClientRect().right}px`;
    };

    positionPopover();
    window.addEventListener("resize", positionPopover);
    window.addEventListener("scroll", positionPopover, true);

    return () => {
      window.removeEventListener("resize", positionPopover);
      window.removeEventListener("scroll", positionPopover, true);
    };
  }, [isCollapsed, isPopoverOpen, sections]);

  if (isCollapsed) {
    return (
      <div className="relative" data-test={`admin-sidebar-${groupId}-popover`}>
        <button
          ref={toggleRef}
          type="button"
          aria-label={label}
          aria-expanded={isPopoverOpen}
          aria-controls={`admin-sidebar-${groupId}-popover-items`}
          data-test={`admin-sidebar-${groupId}-popover-toggle`}
          className={twMerge(adminStyles.sidebar.collapsedLink, theme.myVesselsTableRow.row.hoverTwenty)}
          style={activeStyle}
          onClick={onTogglePopover}
        >
          <AdminSidebarIcon id={groupId} />
        </button>

        {isPopoverOpen && (
          <div
            ref={popoverRef}
            id={`admin-sidebar-${groupId}-popover-items`}
            className={twMerge(adminStyles.sidebar.drawerPopover, adminStyles.sidebar.scrollbar)}
            data-test={`admin-sidebar-${groupId}-popover-items`}
          >
            {sections.map((section) => (
              <NavLink
                key={section.id}
                to={section.path}
                data-test={section.dataTest}
                className={({ isActive }) =>
                  twMerge(
                    "flex h-9 w-full items-center px-4 text-xs transition-colors",
                    theme.uwhcAssistant.text,
                    theme.myVesselsTableRow.row.hoverTwenty,
                    isActive && "font-semibold text-green",
                  )
                }
                style={({ isActive }) =>
                  isActive
                    ? {
                        background: theme.sideBarComponent.background.activeLink,
                        color: theme.sideBarComponent.iconAndText.active,
                      }
                    : undefined
                }
              >
                {section.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-test={`admin-sidebar-${groupId}-drawer`}>
      <button
        type="button"
        className={twMerge(
          adminStyles.sidebar.link,
          adminStyles.sidebar.drawerToggle,
          "justify-between",
          theme.home.tailwind.textDefault,
          theme.myVesselsTableRow.row.hoverTwenty,
          (isDrawerOpen || isActivePath) && adminStyles.sidebar.drawerToggleOpen,
        )}
        aria-expanded={isDrawerOpen}
        aria-controls={`admin-sidebar-${groupId}-drawer-items`}
        data-test={`admin-sidebar-${groupId}-drawer-toggle`}
        onClick={onToggleDrawer}
      >
        <span className="flex min-w-0 items-center gap-3">
          <AdminSidebarIcon id={groupId} />
          <span>{label}</span>
        </span>
        <ChevronDownIcon className={twMerge("h-4 w-4 transition-transform", isDrawerOpen && "rotate-180")} />
      </button>

      {isDrawerOpen && (
        <div id={`admin-sidebar-${groupId}-drawer-items`} data-test={`admin-sidebar-${groupId}-drawer-items`}>
          {sections.map((section) => (
            <NavLink
              key={section.id}
              to={section.path}
              data-test={section.dataTest}
              className={({ isActive }) =>
                twMerge(
                  adminStyles.sidebar.drawerLink,
                  theme.uwhcAssistant.text,
                  theme.myVesselsTableRow.row.hoverTwenty,
                  isActive && "font-semibold text-green",
                )
              }
              style={({ isActive }) =>
                isActive
                  ? {
                      background: theme.sideBarComponent.background.activeLink,
                      color: theme.sideBarComponent.iconAndText.active,
                    }
                  : undefined
              }
            >
              {section.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSidebarDrawerNav;
